/**
 * Premium Shipping Protection Auto-Add System
 * Automatically manages shipping protection based on cart value
 */

(() => {
class ShippingProtection {
  constructor() {
    this.productId = 8799359074473;
    this.surchargeVariantId = 44492347080873;
    this.protectionEnabled = this.getProtectionState();
    this.reconcilePromise = null;
    this.cartCache = null; // Cache cart data to avoid redundant API calls
    this.cacheTimeout = 2000; // Cache valid for 2 seconds
    this.variantIds = [
      46555234762921, 46555234795689, 46555234828457, 46555234861225, 46555234893993,
      46555234926761, 46555234959529, 46555234992297, 46555235025065, 46555235057833,
      46555235090601, 46555235123369, 46555235156137, 46555235188905, 46555235221673,
      46555235254441, 46555235287209, 46555235319977, 46555235352745, 46555235385513,
      46555235418281, 46555235451049, 46555235483817, 46555235516585, 46555235549353,
      46555235582121, 46555235614889, 46555235647657, 46555235680425, 46555235713193,
      46555235745961, 46555235778729, 46555235811497, 46555235844265, 46555235877033,
      46555235909801, 46555235942569, 46555235975337, 46555236008105, 46555236040873,
      46555236073641, 46555236106409, 46555236139177, 46555236171945, 46555236204713,
      46555236237481, 46555236270249, 46555236303017, 46555236335785, 46555236368553,
      46555236401321, 46555236434089, 46555236466857, 46555236499625, 46555236532393,
      46555236565161, 46555236597929, 46555236630697, 46555236663465, 46555236696233,
      46555236729001, 46555236761769, 46555236794537, 46555236827305, 46555236860073,
      46555236892841, 46555236925609, 46555236958377, 46555236991145, 46555237023913,
      46555237056681, 46555237089449, 46555237122217, 46555237154985, 46555237187753,
      46555237220521, 46555237253289, 46555237286057, 46555237318825, 46555237351593,
      46555237384361, 46555237417129, 46555237449897, 46555237482665, 46555237515433,
      46555237548201, 46555237580969, 46555237613737, 46555237646505, 46555237679273,
      46555237712041, 46555237744809, 46555237777577, 46555237810345, 46555237843113,
      46555237875881, 46555237908649, 46555237941417, 46555237974185, 46555238006953
    ];
    
    this.init();
  }

  init() {
    // Only operate on cart page (allows locale-prefixed URLs, e.g. /fr/cart, /fr-ca/cart)
    if (!/^(\/[a-z]{2}(-[a-z]{2})?)?\/cart\/?$/i.test(window.location.pathname)) {
      return;
    }

    const initializeCartPage = () => {
      this.showProtectionSection();
      this.setupCheckboxHandler();
      this.updateProtectionDisplay();
      this.autoAddProtectionOnLoad();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeCartPage, { once: true });
    } else {
      initializeCartPage();
    }
  }

  getCartUrl(endpoint) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${endpoint}`;
  }

  async getCartData(useCache = true) {
    // Use cached data if available and recent
    if (useCache && this.cartCache && (Date.now() - this.cartCache.timestamp) < this.cacheTimeout) {
      return this.cartCache.data;
    }

    // Fetch fresh cart data
    const response = await fetch(this.getCartUrl('cart.js'), {
      credentials: 'same-origin'
    });
    if (!response.ok) {
      throw new Error(`Unable to load cart (${response.status})`);
    }
    const cart = await response.json();
    
    // Cache the result
    this.cartCache = {
      data: cart,
      timestamp: Date.now()
    };

    return cart;
  }

  async getCartSubtotal(useCache = true) {
    const cart = await this.getCartData(useCache);
    return this.getCartSubtotalFromCart(cart);
  }

  async getCurrentProtectionItem(useCache = true) {
    const cart = await this.getCartData(useCache);
    return this.getProtectionItems(cart)[0] || null;
  }

  getProtectionItems(cart) {
    return cart.items.filter(item => Number(item.product_id) === this.productId);
  }

  isSurchargeItem(item) {
    const title = (item.product_title || item.title || '').trim();
    return Number(item.variant_id) === this.surchargeVariantId ||
      item.handle === 'residential-farm-construction-site-delivery' ||
      title === 'Residential/Farm/Construction Site Surcharge';
  }

  getCartSubtotalFromCart(cart) {
    return cart.items.reduce((subtotal, item) => {
      return Number(item.product_id) === this.productId || this.isSurchargeItem(item)
        ? subtotal
        : subtotal + item.line_price;
    }, 0);
  }

  calculateRequiredTier(subtotal) {
    // Each tier covers $1000, starting from $0
    // Tier 1: $0-1000 = $30, Tier 2: $1001-2000 = $60, etc.
    const tier = Math.min(Math.ceil(subtotal / 100000), 100); // Convert cents to dollars and divide by 1000
    return tier;
  }

  getVariantIdForTier(tier) {
    // Tiers are 1-indexed, array is 0-indexed
    return this.variantIds[tier - 1];
  }

  getProtectionState() {
    // Default to enabled (true) unless explicitly disabled
    return localStorage.getItem('shipping_protection_enabled') !== 'false';
  }

  setProtectionState(enabled) {
    this.protectionEnabled = enabled;
    localStorage.setItem('shipping_protection_enabled', enabled.toString());
  }

  getDesiredProtection(cart) {
    // localStorage can be changed by another tab after this instance initializes.
    this.protectionEnabled = this.getProtectionState();
    const subtotal = this.getCartSubtotalFromCart(cart);
    if (!this.protectionEnabled || subtotal <= 0) {
      return { tier: 0, variantId: null };
    }

    const tier = this.calculateRequiredTier(subtotal);
    return {
      tier,
      variantId: this.getVariantIdForTier(tier)
    };
  }

  isProtectionCorrect(cart, desiredVariantId) {
    const protectionItems = this.getProtectionItems(cart);
    if (desiredVariantId === null) {
      return protectionItems.length === 0;
    }

    return protectionItems.length === 1 &&
      Number(protectionItems[0].variant_id) === desiredVariantId &&
      protectionItems[0].quantity === 1;
  }

  buildProtectionUpdates(cart, desiredVariantId) {
    const updates = {};
    let keptDesiredItem = false;

    this.getProtectionItems(cart).forEach(item => {
      const isDesiredItem = desiredVariantId !== null &&
        Number(item.variant_id) === desiredVariantId &&
        !keptDesiredItem;

      updates[item.key] = isDesiredItem ? 1 : 0;
      if (isDesiredItem) keptDesiredItem = true;
    });

    // update.js sets an existing variant to exactly 1, or adds it at 1 if absent.
    // Unlike add.js, repeated or concurrent calls don't increment the quantity.
    if (desiredVariantId !== null && !keptDesiredItem) {
      updates[String(desiredVariantId)] = 1;
    }

    return updates;
  }

  async updateCartQuantities(updates) {
    const response = await fetch(this.getCartUrl('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      throw new Error(`Unable to reconcile shipping protection (${response.status})`);
    }

    const cart = await response.json();
    this.cartCache = { data: cart, timestamp: Date.now() };
    return cart;
  }

  async reconcileProtectionInternal() {
    let cart = await this.getCartData(false);
    let changed = false;
    const initialProtectionCount = this.getProtectionItems(cart).length;

    // update.js returns the complete cart, so use that response for verification.
    // A second update is only needed if a concurrent change left it incorrect.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const desired = this.getDesiredProtection(cart);
      if (this.isProtectionCorrect(cart, desired.variantId)) {
        return { ok: true, changed, initialProtectionCount, cart };
      }

      const updates = this.buildProtectionUpdates(cart, desired.variantId);
      cart = await this.updateCartQuantities(updates);
      changed = true;
    }

    const desired = this.getDesiredProtection(cart);
    return {
      ok: this.isProtectionCorrect(cart, desired.variantId),
      changed,
      initialProtectionCount,
      cart
    };
  }

  setBusy(busy) {
    this.busy = busy;
    document.dispatchEvent(new Event('shipping-protection:state'));
  }

  showError(visible) {
    const error = document.getElementById('shipping-protection-error');
    if (error) error.hidden = !visible;
  }

  async refreshDisplay(cart) {
    cart = cart || await this.getCartData(false);
    await this.updateProtectionDisplay(cart);
    if (typeof window.refreshCartPricingUI === 'function') {
      await window.refreshCartPricingUI(cart);
    }
  }

  async reconcileProtection() {
    if (this.reconcilePromise) {
      return this.reconcilePromise;
    }

    this.setBusy(true);
    this.reconcilePromise = this.reconcileProtectionInternal().catch(error => {
      console.error('Shipping protection safeguard failed:', error);
      return { ok: false, changed: false, initialProtectionCount: 0 };
    });

    try {
      return await this.reconcilePromise;
    } finally {
      this.reconcilePromise = null;
      this.setBusy(false);
    }
  }

  // Public cart-drawer integration used after AJAX add, quantity, and remove
  // operations. Keep the existing CA reconciliation logic and product IDs.
  async updateProtection() {
    const result = await this.reconcileProtection();
    if (result.cart) await this.updateProtectionDisplay(result.cart);
    return result;
  }

  async ensureProtectionForCheckout() {
    if (this.togglePromise) await this.togglePromise;
    // If an on-load reconciliation is running, let it finish before making the
    // checkout-specific fresh pass.
    if (this.reconcilePromise) {
      await this.reconcilePromise;
    }

    return (await this.reconcileProtection()).ok;
  }

  setupCheckboxHandler() {
    const checkbox = document.getElementById('shipping-protection-toggle');
    if (checkbox) {
      checkbox.checked = this.protectionEnabled;
      checkbox.addEventListener('change', (event) => {
        this.showError(false);
        this.setProtectionState(event.target.checked);
        this.toggleVersion = (this.toggleVersion || 0) + 1;
        this.optimisticPending = true;
        window.previewShippingProtection?.();
        if (!this.togglePromise) {
          this.togglePromise = this.syncToggle().finally(() => {
            this.togglePromise = null;
          });
        }
      });
    }
  }

  async syncToggle() {
    let version;
    do {
      version = this.toggleVersion;
      const result = await this.reconcileProtection();
      if (version !== this.toggleVersion) continue;
      try {
        let cart = result.cart;
        if (!result.ok) {
          cart = await this.getCartData(false);
          if (version !== this.toggleVersion) continue;
          this.setProtectionState(this.getProtectionItems(cart).length > 0);
          const checkbox = document.getElementById('shipping-protection-toggle');
          if (checkbox) checkbox.checked = this.protectionEnabled;
        }
        this.optimisticPending = false;
        await this.refreshDisplay(cart);
        this.showError(!result.ok);
      } catch (error) {
        if (version === this.toggleVersion) {
          this.optimisticPending = false;
          window.restoreShippingProtection?.();
          this.showError(true);
        }
      }
    } while (version !== this.toggleVersion);
  }

  showProtectionSection() {
    const section = document.getElementById('shipping-protection-section');
    if (section) {
      section.style.display = 'block';
    }
  }


  async updateProtectionDisplay(cart) {
  cart = cart || await this.getCartData();
  const subtotal = this.getCartSubtotalFromCart(cart);
  const tier = this.calculateRequiredTier(subtotal);

  const coveredValueDollars = tier * 1000; // <-- key change: use tier coverage blocks

  let price;
  if (tier <= 10) {
    price = coveredValueDollars * 0.03; // 3% of covered value
  } else if (tier <= 15) {
    price = 300; // flat
  } else {
    price = coveredValueDollars * 0.02; // 2% of covered value
  }

  // Optional: round to whole dollars for display
  const displayPrice = Math.round(price);

  const priceDisplay = document.getElementById('protection-price-display');
  if (priceDisplay) {
    const costLabel = priceDisplay.dataset.costLabel || 'Cost:';
    priceDisplay.textContent = `${costLabel} $${displayPrice}.00`;
  }

  await this.updateFallbackProtectionDisplay(cart);
}


  async updateFallbackProtectionDisplay(cart) {
    cart = cart || await this.getCartData();
    const subtotal = this.getCartSubtotalFromCart(cart);
    const currentProtection = this.getProtectionItems(cart)[0];
    const fallbackElement = document.getElementById('protection-price-fallback');
    const priceAmountElement = document.getElementById('protection-price-amount');
    
    if (fallbackElement && priceAmountElement) {
      if (this.protectionEnabled && currentProtection && currentProtection.line_price > 0 && subtotal > 0) {
        // Show fallback with current protection price
        const priceFormatted = this.formatMoney(currentProtection.line_price);
        priceAmountElement.textContent = priceFormatted + ' USD';
        fallbackElement.style.display = 'flex';
      } else {
        // Hide fallback
        fallbackElement.style.display = 'none';
      }
    }
  }

  formatMoney(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  async autoAddProtectionOnLoad() {
    const result = await this.reconcileProtection();
    if (!result.ok || !result.changed) return;

    try {
      await this.refreshDisplay(result.cart);
    } catch (error) {
      this.showError(true);
    }
  }

}

// Initialize shipping protection
if (!window.shippingProtection) {
  window.shippingProtection = new ShippingProtection();
}
})();
